import {HTTPClient as HTTPClientCore, IRequestConfig} from '@fanhubmedia/fe-common-utils';
import axios from 'axios';
import {get} from 'lodash';
import {IChangeCaptain, ICreateTeam, IHerd, IJoinTeam} from 'modules/reducers/herds';
import {ILadderAPIOptions, IMonthLadderAPIOptions, IRoundAPIOptions} from 'modules/reducers/trivia_ladders';
import {
	IApiNonTypedResponse,
	IApiResponse,
	IApiResponseRequired,
	IApiResponseRequiredRecord,
	IAudio,
	IBackDoorInfo,
	IChecksum,
	IFooterReducerState,
	IHelpItem, IModelUser,
	IRequestCode,
	IServerTime,
	ISetWagerAction,
	IState,
	IStore,
	ITriviaContests,
	ITriviaStatsContest,
	IUserResponse,
	IUserVerifyCodeResponse,
} from 'modules/types';
import {IBackDoorUser} from "modules/utils/User";
import qs from 'qs';
import {CANCEL} from 'redux-saga';
import {IContestTimes, IScreens} from 'apps/BWW_TRIVIA_WEB/modules/reducers';


export class HTTPClient extends HTTPClientCore {
	/**
	 * Overridden method adds CancelToken symbol, that allow redux-saga'
	 * "takeLatest" function to cancel any requests automatically.
	 */
	public makeRequest<T = any>(config: IRequestConfig): Promise<T> {
		const source = axios.CancelToken.source();

		const new_config = {
			...config,
			params: {
				_: Date.now(),
				...config.params
			},
			cancelToken: source.token
		};

		const request = super.makeRequest(new_config);

		// @ts-ignore
		request[CANCEL] = () => source.cancel();

		return request;
	}
}

const APIClient = new HTTPClient({
	baseURL: process.env.REACT_APP_API_URL || '',
	withCredentials: true,
	transformRequest: [data => qs.stringify(data)],
	onCatchNetworkError: response => get(response, 'response.data')
});

const JSONClient = new HTTPClient({
	baseURL: process.env.REACT_APP_JSON_URL || ''
});

const TERMS_FILE_NAME = 'terms.json';
export const Api = {
	JSON: {
		contests: () => JSONClient.get<ITriviaContests>('contests.json'),
		contests_history: () => JSONClient.get('contests-history.json'),
		faq: () => JSONClient.get<IHelpItem[]>('faq.json'),
		guideline: () => JSONClient.get<IHelpItem[]>('guideline.json'),
		terms: () => JSONClient.get<IHelpItem[]>(TERMS_FILE_NAME),
		prize: () => JSONClient.get<IHelpItem[]>('prize.json'),
		footer: () => JSONClient.get<IFooterReducerState>('footer.json'),
		states: () => JSONClient.get<Record<string, IState>>('states.json'),
		stores: () => JSONClient.get<Record<string, IStore>>('stores.json'),
		checksum: () => JSONClient.get<IChecksum>('checksum.json'),
		screens: () => JSONClient.get('screens.json'),
		screens_usual: () => JSONClient.get<IScreens>('screens_usual_trivia.json'),
		screens_night: () => JSONClient.get<IScreens>('screens_trivia_night.json'),
		contests_timezones: () => JSONClient.get('contests_timezones.json'),
	},
	User: {
		create: (params: IModelUser) => APIClient.post<IApiResponseRequired<IUserResponse>>('user/create', params),
		update: (params: Record<string, unknown>) => APIClient.post<IApiResponseRequiredRecord<IModelUser>>('user/update', params),
		register_for_game: (params: Record<string, unknown>) => APIClient.post('user/register_for_game', params),
		getBettingState: () => APIClient.get("/geo_ip/is_in_legal_betting_state")
	},
	Auth: {
		login: (params: IModelUser) => APIClient.post<IApiResponseRequired<IUserResponse>>('bww_login', params),
		logout: () => APIClient.post<IApiNonTypedResponse>('logout'),
		backdoor: (params: Record<string, unknown>) => APIClient.post<IApiResponseRequired<IBackDoorUser>>('bww_login/backdoor', params),
		request_code: (params: IRequestCode) => APIClient.post('login/request_code', params),
		verify_code: (params: Record<string, unknown>) => APIClient.post<IApiResponseRequired<IUserVerifyCodeResponse>>('login/verify_code', params),
		backdoor_info: () => APIClient.get<IApiResponse<IBackDoorInfo>>('bww_login/backdoor_info'),
	}
};

const TriviaAPIClient = new HTTPClient({
	baseURL: process.env.REACT_APP_TRIVIA_API_URL || '',
	withCredentials: true,
	transformRequest: [data => qs.stringify(data)],
	onCatchNetworkError: response => get(response, 'response.data')
});

export const TriviaApi = {
	Utils: {
		time: () => TriviaAPIClient.get<IApiResponseRequired<IServerTime>>(`time`)
	},
	JSON: {
		audio: () => JSONClient.get<IAudio>('sounds.json'),
		contests_timezones: () => JSONClient.get<IContestTimes>('contests_timezones.json'),
	},
	Trivia: {
		answers: (round_id: number) => {
			return TriviaAPIClient.get(`answers/result/round/${round_id}`)
		}
	},
	leaderboards: {
		/// api/leaderboard/common/contest/{contestId<\d+>}/state/{stateId<\w{2}>}/store/{storeId<\d+>}
		common: ({
					 contest_id,
					 state_id,
					 store_id,
					 region,
					 ...params
				 }: ILadderAPIOptions) => TriviaAPIClient.get(`leaderboard/common/contest/${String(contest_id)}/state/${String(state_id)}/store/${String(store_id)}`, params),

		overall: ({
					  region,
					  ...params
				  }: ILadderAPIOptions) => TriviaAPIClient.get(`leaderboard/overall/region/${String(region)}`, params),

		overall_state: ({
							state_id,
							...params
						}: ILadderAPIOptions) => TriviaAPIClient.get(`leaderboard/overall/state/${String(state_id)}`, params),

		overall_store: ({
							store_id,
							...params
						}: ILadderAPIOptions) => TriviaAPIClient.get(`leaderboard/overall/store/${String(store_id)}`, params),

		month: ({
					month,
					region,
					...params
				}: IMonthLadderAPIOptions) => TriviaAPIClient.get(`leaderboard/month/${String(month)}/region/${String(region)}`, params),

		month_state: ({
						  month,
						  state_id,
						  ...params
					  }: IMonthLadderAPIOptions) => TriviaAPIClient.get(`leaderboard/month/${String(month)}/state/${String(state_id)}`, params),

		month_store: ({
						  month,
						  store_id,
						  ...params
					  }: IMonthLadderAPIOptions) => TriviaAPIClient.get(`leaderboard/month/${String(month)}/store/${String(store_id)}`, params),

		contest: ({contest_id, ...params}: ILadderAPIOptions) =>
			TriviaAPIClient.get(`leaderboard/contest/${String(contest_id)}`, params),

		contest_by_store: ({contest_id, store_id, ...params}: ILadderAPIOptions) =>
			TriviaAPIClient.get(`leaderboard/contest/${String(contest_id)}/store/${String(store_id)}`, params),

		contest_by_state: ({contest_id, state_id, ...params}: ILadderAPIOptions) =>
			TriviaAPIClient.get(`leaderboard/contest/${String(contest_id)}/state/${String(state_id)}`, params),

		round: ({
					round_id,
					...params
				}: IRoundAPIOptions) => TriviaAPIClient.get(`leaderboard/round/${round_id}`, params),
		round_by_store: ({round_id, store_id, ...params}: IRoundAPIOptions) =>
			TriviaAPIClient.get(`leaderboard/round/${round_id}/store/${store_id}`, params),
		round_by_state: ({round_id, state_id, ...params}: IRoundAPIOptions) =>
			TriviaAPIClient.get(`leaderboard/round/${round_id}/state/${state_id}`, params),

		team_contest: ({contest_id, ...params}: ILadderAPIOptions) => {
			return TriviaAPIClient.get(`team/leaderboard/contest/${contest_id}`, params)
		},

		team_contest_store: ({contest_id, store_id, ...params}: ILadderAPIOptions) =>
			TriviaAPIClient.get(`team/leaderboard/contest/${contest_id}/store/${store_id}`, params),

		team_contest_state: ({contest_id, state_id, ...params}: ILadderAPIOptions) =>
			TriviaAPIClient.get(`team/leaderboard/contest/${contest_id}/state/${state_id}`, params),
		team_month: ({
						 month,
						 region,
						 ...params
					 }: IMonthLadderAPIOptions) => TriviaAPIClient.get(`team/leaderboard/month/${month}/region/${region}`, params),

		team_common: ({
						  contest_id,
						  state_id,
						  store_id,
						  region,
						  ...params
					  }: ILadderAPIOptions) => TriviaAPIClient.get(`team/leaderboard/common/contest/${contest_id}/state/${state_id}/store/${store_id}`, params),

		team_month_state: ({
							   month,
							   state_id,
							   ...params
						   }: IMonthLadderAPIOptions) => TriviaAPIClient.get(`team/leaderboard/month/${month}/state/${state_id}`, params),

		team_month_store: ({
							   month,
							   store_id,
							   ...params
						   }: IMonthLadderAPIOptions) => TriviaAPIClient.get(`team/leaderboard/month/${month}/store/${store_id}`, params),
		team_season: ({year, ...params}: ILadderAPIOptions) =>
			TriviaAPIClient.get(`team/leaderboard/season/${year}`, params),
	},
	stats: {
		contest: (contest_id: number) => TriviaAPIClient.get<IApiResponseRequired<ITriviaStatsContest>>(`stats/contest/${contest_id}`),
	},
	User: {
		stats_contest: ({
							contest_id,
							...params
						}: ILadderAPIOptions) => TriviaAPIClient.get(`user/stats/contest/${contest_id}`, params),
		stats_contest_by_month: ({
									 month,
									 region,
									 ...params
								 }: IMonthLadderAPIOptions) => TriviaAPIClient.get(`user/stats/month/${month}/region/${region}`, params),
		play_contest_user: (contest_id: number) => {
			return TriviaAPIClient.post(`play-contest-user/${contest_id}`)
		},
		play_contest_user_count: (contest_id: number) => {
			return TriviaAPIClient.get(`play-contest-user/count/${contest_id}`)
		},
		wager_round: ({round_id, ...rest}: ISetWagerAction) => {
			return TriviaAPIClient.post(`user/wager/round/${round_id}`, rest)
		},
		get_wager_of_round: (round_id: ISetWagerAction['round_id']) => {
			return TriviaAPIClient.get(`user/wager/round/${round_id}`)
		},
	},
	Herds: {
		create: (params: ICreateTeam) => TriviaAPIClient.post<IApiResponseRequiredRecord<IHerd>>('team', params),
		remove: () => TriviaAPIClient.delete<IApiNonTypedResponse>('team'),
		fetch: () => TriviaAPIClient.get<IApiResponseRequiredRecord<IHerd>>('team'),
		join: (params: IJoinTeam) => TriviaAPIClient.post<IApiResponseRequiredRecord<IHerd>>('team/join', params),
		leave: () => TriviaAPIClient.post<IApiNonTypedResponse>('team/leave'),
		revive: () => TriviaAPIClient.post<IApiResponseRequiredRecord<IHerd>>('team/revive'),
		change_captain: (params: IChangeCaptain) => TriviaAPIClient.post<IApiResponseRequiredRecord<IHerd>>('team/change-captain', params),
		delete_player: (player_id: number) => TriviaAPIClient.delete<IApiResponseRequiredRecord<IHerd>>(`team/player/${player_id}`),
	}
};


export * from './ApiError';
